import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client/react';
import {
  openSuccessPromotionAlertModal,
  setProfilePromotionItemId,
  togglePromotionModalView,
  toggleRestoreAlertModal
} from '../actions';
import Bugsnag from '../utils/bugsnag';
import { selectProfilePromotionItemId } from '../store/selectors';
import { payForItemMutation, prolongItemMutation } from '../api/mutations';

const useTogglePromotionModalView = item => {
  const dispatch = useDispatch();
  const router = useRouter();
  const splitPath = router.asPath.split('#');
  const mainPartOfLocation = splitPath[0];
  const restPartOfLocation = splitPath[1];
  const isPremiumView = restPartOfLocation === 'service_feature';
  const isRestoreView = restPartOfLocation === 'restore';
  const isPaymentMethodView = restPartOfLocation === 'limit_payment';
  const isPersonalAdsLimitView = restPartOfLocation === 'personal_ads';
  const { id } = router.query;
  const isPayForLimitByPersonalAdsAvailable = item?.actions?.includes('PAY');
  const isProlongByPersonalAdsPaymentAvailable =
    item?.actions?.includes('PROLONG');
  const { profileItemId } = useSelector(selectProfilePromotionItemId);
  const [prolongItem, { loading: prolongItemLoad }] =
    useMutation(prolongItemMutation);
  const [payForItem, { loading: payForItemLoad }] =
    useMutation(payForItemMutation);

  const onCloseHandler = () => {
    dispatch(togglePromotionModalView(false));
    dispatch(setProfilePromotionItemId(null));

    router.replace(router.asPath.split('#')[0], undefined, {
      shallow: true,
      scroll: false
    });
  };

  const openCorrectView = route => {
    dispatch(togglePromotionModalView(true));

    router.push(`${mainPartOfLocation}#${route}`, undefined, {
      shallow: true,
      scroll: false
    });
  };

  const onToggleRestoreAlertModal = isOpen => {
    dispatch(toggleRestoreAlertModal(isOpen));
  };

  const onOpenRestoreViewHandler = () => {
    if (isProlongByPersonalAdsPaymentAvailable) {
      onToggleRestoreAlertModal(true);
      return;
    }
    openCorrectView('restore');
  };

  const onOpenLimitPaymentViewHandler = () => {
    if (isPayForLimitByPersonalAdsAvailable) {
      openCorrectView('personal_ads');
      return;
    }
    openCorrectView('limit_payment');
  };

  const onDeclineRestorePayment = () => {
    onToggleRestoreAlertModal(false);
  };

  const onAcceptRestorePaymentHandleClick = async () => {
    if (prolongItemLoad) return false;
    onToggleRestoreAlertModal(false);
    prolongItem({
      variables: {
        itemId: id || profileItemId
      }
    })
      .then(data => {
        if (data.data.prolongItem?.item?.state === 'PUBLISHED') {
          dispatch(openSuccessPromotionAlertModal('restore'));
        }
      })
      .catch(error => {
        Bugsnag.notify(error);
      });
  };

  const onLimitPaymentHandleClick = () => {
    if (payForItemLoad) return false;
    payForItem({
      variables: {
        itemId: id || profileItemId
      }
    })
      .then(data => {
        if (data.data.payForItem?.item?.state === 'PENDING') {
          onCloseHandler();
          dispatch(openSuccessPromotionAlertModal('limit_payment'));
        }
        if (data.data.payForItem?.item?.state === 'PUBLISHED') {
          onCloseHandler();
          window.location.reload();
        }
      })
      .catch(error => {
        Bugsnag.notify(error);
      });
  };

  return {
    onCloseHandler,
    openCorrectView,
    splitPath,
    isPremiumView,
    isPaymentMethodView,
    isRestoreView,
    isPersonalAdsLimitView,
    onOpenRestoreViewHandler,
    onOpenLimitPaymentViewHandler,
    onAcceptRestorePaymentHandleClick,
    onDeclineRestorePayment,
    onLimitPaymentHandleClick
  };
};

export default useTogglePromotionModalView;
